import { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Apartment,
  Badge,
  CalendarToday,
  Domain,
  Email,
  Fingerprint,
  HeadsetMic,
  LocationOn,
  People,
  Person,
  Phone,
  Today,
} from "@mui/icons-material";
import { formatDate } from "../../../utils/dateTimeFormat";
import Translate from "../../../utils/Translate";
import { TableAvatar } from "../../../utils/dataGrid";
import { useApi } from "../../../utils/Api";
import { useNavigate } from "react-router-dom";
import SelectActorMatchButton from "../SelectActorMatchButton";
import ButtonWithSpinner from "../../ButtonWithSpinner";
import YesOrNoDialog from "../../YesOrNoDialog";

const ellipsisSx = { textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" };

function joinTruthy(parts, sep = " ") {
  return parts.filter((p) => p != null && p !== "").join(sep);
}

function actorPhone(actor) {
  return actor.mobilePhone?.actorPhoneNumber || actor.businessPhone?.actorPhoneNumber || "";
}

function actorAddressLine(actor) {
  const addr = actor.postalAddress?.actorAddressStreet2 ? actor.postalAddress : actor.invoiceAddress;
  if (!addr) return "";
  return joinTruthy(
    [addr.actorAddressStreet2, joinTruthy([addr.actorAddressZipCode, addr.actorAddressCity], " ")],
    ", ",
  );
}

const getMatchingCompanyDetails = (actor) => [
  { icon: <Apartment />, value: actor.actorName },
  { icon: <Badge />, value: actor.orgNo },
  { icon: <LocationOn />, value: actorAddressLine(actor) },
];

const getMatchingContactDetails = (actor) => [
  { icon: <Person />, value: actor.actorName },
  { icon: <Email />, value: actor.actorEmail },
  { icon: <Phone />, value: actorPhone(actor) },
];

const getMatchingParticipantDetails = (actor) => [
  { icon: <Person />, value: actor.actorName },
  { icon: <Badge />, value: actor.orgNo },
  { icon: <Email />, value: actor.actorEmail },
  { icon: <Phone />, value: actorPhone(actor) },
];

const getBookingRequestCompanyDetails = (bookingRequest) => [
  { icon: <Apartment />, value: bookingRequest.companyName },
  { icon: <Badge />, value: bookingRequest.companyOrgNo },
  {
    icon: <LocationOn />,
    value: joinTruthy(
      [bookingRequest.companyStreet2, joinTruthy([bookingRequest.companyZipCode, bookingRequest.companyCity], " ")],
      ", ",
    ),
  },
];

const getBookingRequestContactDetails = (bookingRequest) => [
  { icon: <Person />, value: bookingRequest.contactName },
  { icon: <Email />, value: bookingRequest.contactEmail },
  { icon: <Phone />, value: bookingRequest.contactPhone },
];

function ParticipantCard({ participant, selectedActorId, onSelectMatch }) {
  const theme = useTheme();

  const selectedMatchingActor = selectedActorId
    ? participant.matchingParticipants.find((x) => x.actorId === selectedActorId)
    : null;
  const displayedName = selectedMatchingActor ? selectedMatchingActor.actorName : participant.name;
  const displayedSsn = selectedMatchingActor ? selectedMatchingActor.orgNo : participant.ssn;
  const displayedEmail = selectedMatchingActor ? selectedMatchingActor.actorEmail : participant.email;
  const displayedPhone = selectedMatchingActor ? actorPhone(selectedMatchingActor) : participant.phone;

  return (
    <Card
      sx={{ padding: "16px", transform: "translateZ(0)", borderRadius: "4px", height: "100%" }}
      style={{ boxShadow: theme.customShadows.z16 }}
    >
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1.5}>
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
            <Person />
            <Tooltip title={displayedName} arrow>
              <Box sx={{ ...ellipsisSx, minWidth: 0 }}>{displayedName}</Box>
            </Tooltip>
          </Stack>
          <SelectActorMatchButton
            originalDetails={[
              { icon: <Person />, value: participant.name },
              { icon: <Fingerprint />, value: participant.ssn },
              { icon: <Email />, value: participant.email },
              { icon: <Phone />, value: participant.phone },
            ]}
            matches={participant.matchingParticipants ?? []}
            selectedActorId={selectedActorId}
            onSelect={onSelectMatch}
            getMatchDetails={getMatchingParticipantDetails}
          />
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.5}>
          <Fingerprint />
          <Tooltip title={displayedSsn} arrow>
            <Box sx={ellipsisSx}>{displayedSsn}</Box>
          </Tooltip>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.5}>
          <Email />
          <Tooltip title={displayedEmail} arrow>
            <Box sx={ellipsisSx}>{displayedEmail}</Box>
          </Tooltip>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1.5}>
          <Phone />
          <Tooltip title={displayedPhone} arrow>
            <Box sx={ellipsisSx}>{displayedPhone}</Box>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}

function EmptyParticipantCard({ nbrOfEmptyParticipants }) {
  const theme = useTheme();
  const label =
    nbrOfEmptyParticipants > 1
      ? `${Translate.get("EmptySlot")} (x${nbrOfEmptyParticipants})`
      : Translate.get("EmptySlot");

  return (
    <Card
      sx={{ padding: "16px", transform: "translateZ(0)", borderRadius: "4px", height: "100%" }}
      style={{ boxShadow: theme.customShadows.z16 }}
    >
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%", minHeight: "168px" }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {label}
        </Typography>
      </Grid>
    </Card>
  );
}

export default function ReviewPreliminaryBookingDialog({ bookingRequest, courseId, onClose }) {
  const [showParticipants, setShowParticipants] = useState(true);
  const [selectedCompanyActorId, setSelectedCompanyActorId] = useState(null);
  const [selectedContactActorId, setSelectedContactActorId] = useState(null);
  const [selectedParticipantActorIds, setSelectedParticipantActorIds] = useState({});
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const api = useApi();
  const navigate = useNavigate();

  function setParticipantSelection(bookingRequestParticipantId, actorId) {
    setSelectedParticipantActorIds((prev) => {
      const next = { ...prev };
      if (actorId === null) {
        delete next[bookingRequestParticipantId];
      } else {
        next[bookingRequestParticipantId] = actorId;
      }
      return next;
    });
  }

  async function handleDelete() {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}courses/bookings/requests/${bookingRequest.bookingRequestId}`,
      true,
      "DELETE",
    );
    if (response.isSuccessful) {
      onClose(true);
    }
  }

  async function handleConfirm() {
    const response = await api.fetch(
      `${import.meta.env.VITE_MAIN_URL}courses/bookings/requests/confirm`,
      {
        courseId,
        bookingRequestId: bookingRequest.bookingRequestId,
        selectedCompanyActorId,
        selectedContactActorId,
        selectedParticipantActorIds,
      },
      "POST",
    );
    if (response.isSuccessful) {
      onClose(true);
    }
  }

  const selectedMatchingCompany = selectedCompanyActorId
    ? bookingRequest.matchingCompanies.find((x) => x.actorId === selectedCompanyActorId)
    : null;
  const diplayedCompanyName = selectedMatchingCompany ? selectedMatchingCompany.actorName : bookingRequest.companyName;

  const selectedMatchingContact = selectedContactActorId
    ? bookingRequest.matchingContacts.find((x) => x.actorId === selectedContactActorId)
    : null;
  const diplayedContactName = selectedMatchingContact ? selectedMatchingContact.actorName : bookingRequest.contactName;
  const diplayedContactEmail = selectedMatchingContact
    ? selectedMatchingContact.actorEmail
    : bookingRequest.contactEmail;
  const diplayedContactPhone = selectedMatchingContact
    ? actorPhone(selectedMatchingContact)
    : bookingRequest.contactPhone;

  return (
    <Dialog open onClose={() => onClose(false)} maxWidth="md" fullWidth scroll="paper" className="tightMobileDialog">
      <DialogTitle>{Translate.get("ReviewPreliminaryBooking")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1, mb: 4 }}>
          <Stack alignItems="center" direction="row" gap={2}>
            <Avatar>
              <Domain />
            </Avatar>
            <Tooltip title={diplayedCompanyName} arrow>
              <Box className="tableAvatarTitle bold" sx={ellipsisSx}>
                {diplayedCompanyName}
              </Box>
            </Tooltip>
            <SelectActorMatchButton
              originalDetails={getBookingRequestCompanyDetails(bookingRequest)}
              matches={bookingRequest.matchingCompanies ?? []}
              selectedActorId={selectedCompanyActorId}
              onSelect={setSelectedCompanyActorId}
              getMatchDetails={getMatchingCompanyDetails}
              isCompany
            />
          </Stack>
          <Stack paddingLeft="17px" alignItems="center" direction="row" gap={2}>
            <HeadsetMic />
            <Box sx={ellipsisSx}>
              <TableAvatar
                hideImage
                title={diplayedContactName}
                subtitle={[<a href={"mailto:" + diplayedContactEmail}>{diplayedContactEmail}</a>, diplayedContactPhone]}
              />
            </Box>
            <SelectActorMatchButton
              originalDetails={getBookingRequestContactDetails(bookingRequest)}
              matches={bookingRequest.matchingContacts ?? []}
              selectedActorId={selectedContactActorId}
              onSelect={setSelectedContactActorId}
              getMatchDetails={getMatchingContactDetails}
            />
          </Stack>
          <Stack paddingLeft="17px" alignItems="center" direction="row" gap={2}>
            <People />
            <Box>{bookingRequest.nbrOfParticipants}</Box>
          </Stack>
          <Stack paddingLeft="17px" alignItems="center" direction="row" gap={2}>
            <Today />
            <Box>{formatDate(bookingRequest.bookingDate)}</Box>
          </Stack>
        </Stack>

        <Divider />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Stack sx={{ mb: 2 }} alignItems="center" justifyContent="space-between" direction="row" gap={2}>
            <span className="tableAvatarTitle bold">
              {Translate.get("Students")} ({bookingRequest.nbrOfParticipants})
            </span>
            <Button variant="text" size="small" onClick={() => setShowParticipants((prev) => !prev)}>
              {showParticipants ? Translate.get("Hide") : Translate.get("Show")}
            </Button>
          </Stack>
          {showParticipants && (
            <Grid container spacing={2}>
              {bookingRequest.participants.map((participant, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                  <ParticipantCard
                    participant={participant}
                    selectedActorId={
                      participant.bookingRequestParticipantId in selectedParticipantActorIds
                        ? selectedParticipantActorIds[participant.bookingRequestParticipantId]
                        : null
                    }
                    onSelectMatch={(actorId) =>
                      setParticipantSelection(participant.bookingRequestParticipantId, actorId)
                    }
                  />
                </Grid>
              ))}
              {(bookingRequest.participants?.length ?? 0) < bookingRequest.nbrOfParticipants && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <EmptyParticipantCard
                    nbrOfEmptyParticipants={
                      bookingRequest.nbrOfParticipants - (bookingRequest.participants?.length ?? 0)
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowConfirmDelete(true)} color="error">
          {Translate.get("Delete")}
        </Button>
        <Button onClick={() => onClose(false)}>{Translate.get("Cancel")}</Button>
        <ButtonWithSpinner onClick={handleConfirm} variant="contained">
          {Translate.get("ConfirmPreliminaryBooking")}
        </ButtonWithSpinner>
      </DialogActions>
      <YesOrNoDialog
        open={showConfirmDelete}
        title={Translate.get("Delete")}
        text={Translate.get("SureDeletePreliminaryBooking")}
        onNo={() => setShowConfirmDelete(false)}
        onYes={async () => {
          setShowConfirmDelete(false);
          await handleDelete();
        }}
      />
    </Dialog>
  );
}
